import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Fade } from "react-awesome-reveal";
import Loading from "../../components/common/loading";
import ChartIcon from "../../assets/icons/chart.svg";
import useStore from "../../features/store";

const Biorhythm = () => {
  const {
    isLoadingProfile,
    profile,
    isLoadingBiorhythm,
    biorhythm,
    fetchBiorhythm,
  }: any = useStore();

  useEffect(() => {
    if (profile?.birth_day && !biorhythm) {
      fetchBiorhythm();
    }
  }, [profile]);

  const normaliseValue = (value: number | string) => {
    return Number(value) >= 100 ? 99 : value;
  };

  const RadialProgress = (value: number) => {
    return (
      <div className="absolute left-10 -top-[14%] lg:-top-[18%] drop-shadow-xl flex justify-center my-6">
        <div
          className="radial-progress bg-white text-black"
          style={{
            direction: "ltr",
            //@ts-ignore
            "--value": Math.abs(value * 100),
            "--size": "100px",
            "--thickness": ".15rem",
          }}
          role="progressbar"
        >
          <span className="mt-2 font-rokh text-2xl font-[900]">
            {normaliseValue(Math.round(value * 100))}
            <strong className="text-base">%</strong>
          </span>
        </div>
      </div>
    );
  };

  const OtherRhythms = (title: string, value: number, info: string) => {
    return (
      <div className="flex flex-col items-center justify-between p-5 rounded-lg bg-pr_yellow flex-1 w-full">
        <div className="flex items-center justify-between w-full">
          <h5 className="font-rokh text-lg font-[900]">{title}</h5>
          <h6
            className="text-xl font-rokh text-xl font-[900]"
            style={{ direction: "ltr" }}
          >
            {normaliseValue(Math.round(value * 100))}
            <strong className="text-base">%</strong>
          </h6>
        </div>

        <p className="mt-1 text-sm text-right w-full">{info}</p>
      </div>
    );
  };

  return (
    <div className="flex-1 mt-5">
      <div className="mb-20">
        <div>
          <div className="flex items-center mb-3">
            <img src={ChartIcon} width={28} alt="بایوریتم" />
            <h1 className="font-[800] mr-3 mt-2 font-rokh text-lg">بایوریتم</h1>
          </div>
          <p>
            بایوریتم (یا چرخه زیستی) به ایده‌ای اشاره داره که بر اساس اون زندگی
            انسان تحت تأثیر سه چرخه تکرارشونده اصلی یعنی جسمی، احساسی و ذهنی
            قرار می‌گیره. این چرخه‌ها از زمان تولد شروع می‌شن و در طول زندگی، به
            صورت ریتمیک ادامه دارن. بایوریتم‌ها ابزار ساده‌ای برای پیش‌بینی
            روزهای پربازده یا کم‌انرژی فرد هستن و ممکنه در زمینه‌هایی مثل
            برنامه‌ریزی شغلی، ورزشی یا حتی روابط مورد استفاده قرار بگیرن. برای
            اینکه اطلاعات بیشتری درباره بایوریتم داشته باشی، خوندن{" "}
            <a
              target="_blank"
              href="https://bodybon.info/what-is-biorhythm/"
              className="font-bold text-pr_primary"
            >
              {" "}
              این مقاله{" "}
            </a>{" "}
            بادی‌بان رو پیشنهاد می‌کنیم.
          </p>
        </div>
      </div>
      {!biorhythm && !profile?.birth_day && (
        <Fade triggerOnce>
          {isLoadingProfile || isLoadingBiorhythm ? (
            <Loading type="SimpleTextLoading" />
          ) : (
            <div className="max-w-xl mx-auto border-[1px] border-gray-500 p-10 pb-16 rounded-2xl">
              <p className="font-rokh font-[900] text-lg text-center">
                برای دریافت منحنی بایوریتم، تاریخ تولدت رو به پروفایل اضافه کن.
              </p>
              <div className="flex items-center justify-center mt-3">
                <Link
                  to="/profile/update?redirect=biorhythm"
                  className="btn btn-wide mx-auto mt-5 font-rokh font-[900] pt-2"
                >
                  ویرایش پروفایل
                </Link>
              </div>
            </div>
          )}
        </Fade>
      )}

      {biorhythm && (
        <div className="mt-10">
          <Fade triggerOnce>
            <div className="flex flex-col lg:flex-row items-center justify-center gap-3">
              <div className="bg-pr_purple relative rounded-2xl p-10 flex-1">
                {RadialProgress(
                  Number(Number(biorhythm?.intellectual).toFixed(2))
                )}
                <h2 className="font-rokh mb-4 text-3xl font-[900]">ذهنی</h2>
                <p className="text-right">
                  فکر کن یه روز ذهنت مثل یه اتوبان خلوت کار می‌کنه، همه‌چیز سریع
                  و روانه. توی اوج چرخه ذهنی، مغزت مثل یه ابزار دقیق و سریع
                  می‌شه؛ ایده‌ها به راحتی شکل می‌گیرن، مسائل پیچیده حل می‌شن، و
                  یادگیری جدید برات لذت‌بخش‌تره. اما وقتی چرخه رو به پایینه،
                  ممکنه حس کنی افکارت مبهم شدن، حافظه‌ات یاری نمی‌کنه، و حتی
                  ساده‌ترین تصمیمات برات سخت‌تر می‌شن. این چرخه که هر ۳۳ روز
                  یک‌بار کامل می‌شه، روی خلاقیت، توانایی حل مسئله، و سرعت
                  یادگیری تأثیر می‌ذاره.
                </p>
              </div>

              <div className="bg-pr_green relative rounded-2xl p-10 flex-1 my-14 lg:my-0">
                {RadialProgress(
                  Number(Number(biorhythm?.emotional).toFixed(2))
                )}
                <h2 className="font-rokh mb-4 text-3xl font-[900]">احساسی</h2>
                <p className="text-right">
                  روزهایی هست که با هر چیز کوچیکی ذوق می‌کنی یا مهربون‌تر از
                  همیشه‌ای، و روزهایی هم هست که دلگیرتر و حساس‌تر می‌شی. این‌ها
                  به چرخه احساسی ربط دارن که هر ۲۸ روز تغییر می‌کنه. وقتی توی
                  اوج این چرخه‌ای، احساسات مثبت مثل عشق، خوش‌بینی، و خلاقیت در
                  اوج خودشونه. ولی وقتی توی پایین این چرخه‌ای، ممکنه زودتر از
                  کوره در بری، احساس تنهایی کنی، یا حتی برای ارتباط با دیگران
                  کم‌انرژی‌تر باشی. این چرخه تأثیر زیادی روی حالات روحی، روابط،
                  و توانایی پذیرش احساسات داره.
                </p>
              </div>

              <div className="bg-pr_blue relative rounded-2xl p-10 flex-1">
                {RadialProgress(Number(Number(biorhythm?.physical).toFixed(2)))}
                <h2 className="font-rokh mb-4 text-3xl font-[900]">فیزیکی</h2>
                <p className="text-right">
                  اوج و فرودهای انرژی جسمی‌ات معمولاً با این چرخه ۲۳ روزه
                  هماهنگه. توی روزهای اوج، انگار قدرت بدنی و استقامتت بیشتر شده،
                  ورزش کردن برات آسون‌تره، و حتی کم‌خوابی یا فشار زیاد رو بهتر
                  تحمل می‌کنی. ولی توی روزهای پایین این چرخه، احساس خستگی بیشتری
                  داری، فعالیت‌های فیزیکی سخت‌تر به نظر می‌رسن، و شاید نیاز به
                  استراحت بیشتری حس کنی. چرخه جسمی روی توانایی جسمانی، هماهنگی
                  حرکتی، و مقاومت بدن در برابر بیماری‌ها اثر می‌ذاره و بهتره توی
                  کارها و ورزش‌ها بهش توجه کنی.
                </p>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row items-center justify-between my-5 gap-3">
              {OtherRhythms(
                "زیبایی‌شناسی",
                Number(Number(biorhythm?.aesthetic).toFixed(2)),
                "چرخه 43 روزه و به درک زیبایی در هنر و طبیعت مربوط می‌شود"
              )}

              {OtherRhythms(
                "آگاهی",
                Number(Number(biorhythm?.awareness).toFixed(2)),
                "چرخه 48 روزه و بر میزان توجه و هوشیاری فرد تأثیر می‌گذارد."
              )}

              {OtherRhythms(
                "شهودی",
                Number(Number(biorhythm?.spiritual).toFixed(2)),
                "چرخه 38 روزه و به توانایی دریافت و تفسیر احساسات و الهامات ربط دارد."
              )}

              {OtherRhythms(
                "معنوی",
                Number(Number(biorhythm?.aesthetic).toFixed(2)),
                " چرخه 53 روزه به درک و ارتباطات معنوی و احساسی عمیق اشاره دارد."
              )}
            </div>
          </Fade>
        </div>
      )}
    </div>
  );
};

export default Biorhythm;
